import InfoTip from '../../components/InfoTip';
import React, { FC } from 'react';
import {
  Alert,
  Box,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { tokens } from '../../theme';
import { SourceEfficiencyData } from '../../helpers/webinarStatisticsData';
import { formatNumber } from '../../helpers/numberUtils';

interface SourceEfficiencyTabProps {
  colors: ReturnType<typeof tokens>;
  data: SourceEfficiencyData | null;
}

/** "—" statt 0, wo der Nenner fehlt. Eine fehlende Reichweite ist keine Aussage. */
const dash = (v: number | null | undefined, digits = 0): string =>
  v === null || v === undefined ? '—' : formatNumber(v, digits);

const SourceEfficiencyTab: FC<SourceEfficiencyTabProps> = ({ colors, data }) => {
  if (!data) return null;
  const { rows, meta, totals } = data;

  // Anteil der Anfos, der ueberhaupt in einer Quote stehen kann. Schwankt stark je Fenster
  // (gemessen 2026-08-10: 83 % Mai–Jun, 68 % Jul–Aug) — deshalb angezeigt, nicht nur erklaert.
  const coverageAnfos = totals.anfos > 0 ? (100 * totals.covered_anfos) / totals.anfos : null;

  const head = {
    color: 'black',
    backgroundColor: '#e0e0e0 !important',
    fontWeight: 'bold',
  } as const;

  return (
    // minWidth: 0 ist das Entscheidende: ohne das weigert sich der Flex-Kandidat, unter seine
    // min-content-Breite zu schrumpfen, und die 8-spaltige Tabelle schiebt die GANZE Seite
    // horizontal auf (gemessen bei 834 px: Dokument 1011 px breit). Mit minWidth: 0 greift das
    // overflow-x des TableContainers und nur die Tabelle scrollt.
    <Box sx={{ minWidth: 0, maxWidth: '100%' }}>
      <Typography variant="h6" sx={{ color: colors.grey[100], mb: 1 }}>
        Quellen-Effizienz — {meta.publishers.length === 1 ? 'nur ' : ''}
        {meta.publisher}
      </Typography>
      {meta.publishers_without_newsletter_reach.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Für{' '}
          {meta.publishers_without_newsletter_reach
            .map((p) => meta.publisher_options.find((o) => o.key === p)?.label ?? p)
            .join(', ')}{' '}
          liegen keine Newsletter-Ausgabendaten vor. Registrierungen und Anfos dieser Marken sind
          vollständig enthalten; Newsletter-Zeilen, zu denen sie beitragen, zeigen deshalb „—“ statt
          einer Quote. Die Mailing-Zeilen haben für alle Marken einen Nenner.
        </Alert>
      )}
      <Typography variant="body2" sx={{ color: colors.grey[300], mb: 2 }}>
        Anfos je 1.000 Werbekontakte. Registrierungen und Anfos summieren auf die Gesamtzahl unten.
        Die <strong>Werbekontakte nicht</strong> — trägt eine Ausgabe zwei Plätze, zählen ihre
        Kontakte in beiden Zeilen.
      </Typography>
      <Alert
        severity={coverageAnfos !== null && coverageAnfos < 60 ? 'warning' : 'info'}
        sx={{ mb: 2 }}
      >
        Eine Quote gibt es nur, wo Zähler <em>und</em> Nenner aus demselben Versand stammen. Im
        gewählten Zeitraum trifft das auf{' '}
        <strong>
          {formatNumber(totals.covered_anfos, 0)} von {formatNumber(totals.anfos, 0)} Anfos
        </strong>
        {coverageAnfos !== null && ` (${formatNumber(coverageAnfos, 0)} %)`} zu. Der Rest steht in
        der letzten Zeile: Newsletter-Registrierungen von vor {meta.reach_data_floor}, für die es
        noch keine Ausgabendaten gibt. Die Quelle ist dort bekannt, es fehlt nur der Nenner.
      </Alert>
      {meta.reaktivierungs_sends > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {meta.reaktivierungs_sends} der Versände im Zeitraum sind{' '}
          <strong>Reaktivierungs-Versände</strong> — samstags, mit zusätzlich seit über drei Monaten
          inaktiven Empfängern. Sie treiben die Empfängerzahl aufs Zwei- bis Dreifache.{' '}
          {meta.reach_basis === 'raw' ? (
            <>
              Aktuell zählt <strong>jeder angeschriebene Kontakt</strong>. Über „Werbekontakte“ oben
              lässt sich auf den aktiven Teil umstellen — die Quoten fallen dann höher aus.
            </>
          ) : (
            <>
              Aktuell zählt <strong>nur der aktive Teil</strong> der Empfänger. Die Anmeldungen aus
              dem inaktiven Teil bleiben im Zähler, die Quoten fallen dadurch etwas zu gut aus. Für
              die Frage „Anfos je verbrauchtem Werbekontakt“ ist „Alle angeschriebenen“ richtig.
            </>
          )}
        </Alert>
      )}
      {totals.residual_negative && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Die Einzelzeilen liegen über der Gesamtzahl. Das darf nicht vorkommen und heißt, dass sich
          die Zähl-Definitionen widersprechen — die Quoten unten sind dann nicht belastbar.
        </Alert>
      )}

      {!meta.reach_available && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Für den gewählten Zeitraum gibt es keine Reichweitendaten. Newsletter-Ausgaben sind erst
          ab {meta.reach_data_floor} erfasst.
        </Alert>
      )}
      {meta.reach_clamped && meta.reach_available && meta.reach_window && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Die Newsletter-Zeilen sind auf {meta.reach_window.from} bis {meta.reach_window.to}{' '}
          eingeschränkt — davor gibt es keine Ausgabendaten. Die Mailing-Zeilen decken den ganzen
          gewählten Zeitraum ab. Quoten aus unterschiedlichen Fenstern wären nicht vergleichbar.
        </Alert>
      )}

      <TableContainer
        component={Paper}
        sx={{ backgroundColor: '#ffffff', maxWidth: '100%', overflowX: 'auto' }}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={head}>
                Quelle
                <InfoTip text="Werbeweg, über den die Anmeldung kam (Heat-Up-Mailing, Redlink, Newsletter-Platz …)." />
              </TableCell>
              <TableCell sx={head} align="right">
                Versände / Ausgaben
                <InfoTip text="Wie viele Mailing-Versände bzw. Newsletter-Ausgaben diese Zeile speisen." />
              </TableCell>
              <TableCell sx={head} align="right">
                Werbekontakte
                <InfoTip text="Empfänger dieser Versände bzw. Ausgaben — der Nenner der Je-1.000-Werte." />
              </TableCell>
              <TableCell sx={head} align="right">
                Registrierungen
                <InfoTip text="Webinar-Registrierungen (Personen), deren Quelle das Affiliate ihrer ersten Anmeldung ist." />
              </TableCell>
              <TableCell sx={head} align="right">
                Anfos
                <InfoTip text="Bestellungen von Personen, die über diese Quelle angemeldet waren — erste Bestellung je Person und Webinar, nach der Anmeldung. Deshalb weniger als in der Umsatz-Attribution, die jede dem Webinar zugeordnete Bestellung zählt." />
              </TableCell>
              <TableCell sx={head} align="right">
                Reg. / 1.000
                <InfoTip text="Registrierungen je 1.000 Werbekontakte." />
              </TableCell>
              <TableCell sx={head} align="right">
                Anfos / 1.000
                <InfoTip text="Anfos je 1.000 Werbekontakte — der Effizienzwert dieser Quelle." />
              </TableCell>
              <TableCell sx={head} align="right">
                Anfos / Reg.
                <InfoTip text="Anfos je Registrierung: Anfos ÷ Registrierungen × 100." />
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => {
              // Unter 30 Anfos ist eine Quote nicht belastbar — sichtbar machen, statt sie
              // wie eine gemessene Zahl aussehen zu lassen. Der Nulltreffer MUSS mitgemeint sein:
              // „0,00 %" aus 30 Anmeldungen liest sich wie ein Messergebnis, ist aber von 3 %
              // nicht zu unterscheiden. Anker ist deshalb die Anmeldung, nicht die Anfo — Zeilen
              // ganz ohne Anmeldungen zeigen ohnehin „—".
              const thin = r.anmeldungen > 0 && r.anfos < 30;
              return (
                <TableRow key={r.quelle}>
                  <TableCell sx={{ color: 'black' }}>
                    {r.quelle}
                    {r.no_reach_reason && (
                      <Tooltip title={r.no_reach_reason}>
                        <Chip
                          label="keine Reichweite"
                          size="small"
                          sx={{ ml: 1, backgroundColor: colors.grey[600], color: 'white' }}
                        />
                      </Tooltip>
                    )}
                    {r.reach_reconstructed && (
                      <Tooltip title="Für die Newsletter der anderen Portale gibt es kein Protokoll, welche Ausgabe die Webinar-Anzeige getragen hat. Der Nenner umfasst deshalb die Ausgaben, die bis zu drei Tage vor einem Webinar erschienen sind — abgeleitet aus den Anmelde-Tokens, bei denen dieser Abstand 2026 ausnahmslos zutraf. Die Empfängerzahlen selbst sind gemessen, nur die Auswahl der Ausgaben ist rekonstruiert.">
                        <Chip
                          label="Nenner rekonstruiert"
                          size="small"
                          sx={{ ml: 1, backgroundColor: colors.blueAccent[700], color: 'white' }}
                        />
                      </Tooltip>
                    )}
                    {r.einheiten_ohne_reichweite > 0 && (
                      <Tooltip
                        title={`${r.einheiten_ohne_reichweite} Ausgabe(n) ohne Reichweitendaten — nicht im Nenner enthalten.`}
                      >
                        <Chip
                          label={`${r.einheiten_ohne_reichweite} ohne Daten`}
                          size="small"
                          sx={{ ml: 1, backgroundColor: colors.redAccent[700], color: 'white' }}
                        />
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell sx={{ color: 'black' }} align="right">
                    {r.einheiten || '—'}
                  </TableCell>
                  <TableCell sx={{ color: 'black' }} align="right">
                    {dash(r.kontakte)}
                  </TableCell>
                  <TableCell sx={{ color: 'black' }} align="right">
                    {formatNumber(r.anmeldungen, 0)}
                  </TableCell>
                  <TableCell sx={{ color: 'black' }} align="right">
                    {formatNumber(r.anfos, 0)}
                  </TableCell>
                  <TableCell sx={{ color: 'black' }} align="right">
                    {dash(r.anmeldungen_pro_1k, 2)}
                  </TableCell>
                  <TableCell sx={{ color: 'black', fontWeight: 'bold' }} align="right">
                    {dash(r.anfos_pro_1k, 3)}
                  </TableCell>
                  <TableCell sx={{ color: 'black' }} align="right">
                    {r.anfo_quote === null ? '—' : `${formatNumber(r.anfo_quote, 2)} %`}
                    {thin && (
                      <Tooltip
                        title={
                          r.anfos === 0
                            ? `Keine einzige Anfo aus ${formatNumber(r.anmeldungen, 0)} Registrierungen — das ist kein gemessenes 0 %, sondern zu wenig Datenbasis.`
                            : `Nur ${r.anfos} Anfos — statistisch nicht belastbar.`
                        }
                      >
                        <Chip
                          label="dünn"
                          size="small"
                          sx={{ ml: 1, backgroundColor: colors.grey[600], color: 'white' }}
                        />
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
          {/* Gesamtzeile. Bewusst NUR die additiven Spalten: Registrierungen und Anfos sind je
              (Webinar, Adresse) wechselseitig exklusiv und summieren deshalb sauber. Werbekontakte
              und die abgeleiteten Je-1.000-Werte tun das nicht — eine Ausgabe mit zwei Plätzen
              zählt ihre Kontakte in zwei Zeilen. Dort steht „—“ statt einer erfundenen Summe. */}
          <TableFooter>
            <TableRow sx={{ backgroundColor: '#f0f0f0' }}>
              <TableCell sx={{ color: 'black', fontWeight: 'bold', fontSize: '0.875rem' }}>
                <Tooltip
                  title={
                    `Alle Registrierungen, die über eine Quelle von ${meta.publisher} kamen — maßgeblich ` +
                    'ist das Affiliate der ANMELDUNG. Der Tab „Umsatz-Attribution“ zählt bewusst ' +
                    'anders: dort zählt das Banner der BESTELLUNG, unabhängig davon, wer die ' +
                    'Anmeldung gebracht hat. Für die vier Juli-Webinare 2026: 128 dort — davon 92 ' +
                    'mit FT-Banner, davon 76 von einem Registrierten, davon 53 über eine FT-Quelle. ' +
                    'Der Rest sind Käufer, die KME, Börse-Express, Börse-Global oder Stock-World zum ' +
                    'Webinar gebracht hat; die FT-gebrandete Webinar-Seite stempelt die Bestellung ' +
                    'trotzdem mit FT.'
                  }
                >
                  <span style={{ cursor: 'help', borderBottom: '1px dotted #666' }}>
                    Gesamt ({meta.publishers.length === 1 ? meta.publisher : 'gewählte Marken'})
                  </span>
                </Tooltip>
              </TableCell>
              <TableCell sx={{ color: 'black' }} align="right">
                —
              </TableCell>
              <TableCell sx={{ color: 'black' }} align="right">
                <Tooltip title="Werbekontakte lassen sich nicht summieren: eine Ausgabe, die zwei Plätze trägt, zählt ihre Kontakte in beiden Zeilen.">
                  <span>—</span>
                </Tooltip>
              </TableCell>
              <TableCell
                sx={{ color: 'black', fontWeight: 'bold', fontSize: '0.875rem' }}
                align="right"
              >
                {formatNumber(totals.anmeldungen, 0)}
              </TableCell>
              <TableCell
                sx={{ color: 'black', fontWeight: 'bold', fontSize: '0.875rem' }}
                align="right"
              >
                {formatNumber(totals.anfos, 0)}
              </TableCell>
              <TableCell sx={{ color: 'black' }} align="right">
                —
              </TableCell>
              <TableCell sx={{ color: 'black' }} align="right">
                —
              </TableCell>
              <TableCell
                sx={{ color: 'black', fontWeight: 'bold', fontSize: '0.875rem' }}
                align="right"
              >
                {totals.anfo_quote === null ? '—' : `${formatNumber(totals.anfo_quote, 2)} %`}
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>

      <Typography variant="caption" sx={{ color: colors.grey[300], mt: 2, display: 'block' }}>
        {meta.overlap_note} Zuordnung ist Single-Touch: wer eine Anzeige sieht und sich später über
        eine andere Quelle anmeldet, zählt nur bei der ersten Anmeldung — Plätze weiter vorn im
        Trichter werden dadurch systematisch unterschätzt.
      </Typography>
      <Typography variant="caption" sx={{ color: colors.grey[300], mt: 1, display: 'block' }}>
        <strong>Warum hier weniger Anfos stehen als unter „Umsatz-Attribution“:</strong> dort zählt
        das Banner der Bestellung, hier die Quelle der Anmeldung. Wen KME, Börse-Express,
        Börse-Global oder Stock-World zum Webinar gebracht hat, erzeugt beim Kauf trotzdem ein
        FT-Banner — weil die Webinar-Seite FT-gebrandet ist. Diese Abschlüsse gehören keiner
        FT-Quelle und stehen deshalb nicht in dieser Tabelle. Beispiel Juli 2026: 128 dort → 92 mit
        FT-Banner → 76 von einem Registrierten → 53 über eine FT-Quelle.
      </Typography>
    </Box>
  );
};

export default SourceEfficiencyTab;
